import { Widget, WidgetOrderUpdate } from '../types/widget';
import { getConnectionId } from '../utils/storage';
import { authFetch } from '../utils/api';

export class WidgetService {
    private static baseUrl = process.env.REACT_APP_API_URL;

    /**
     * Save widget order to backend
     * @param userId - User ID
     * @param widgets - Array of widgets in their new order
     * @returns Promise<boolean> - Success status
     */
    static async saveWidgetOrder(userId: string, widgets: Widget[], collectionId?: string | null): Promise<boolean> {
        try {
            const orderData: WidgetOrderUpdate = {
                user_id: userId,
                collection_id: collectionId || undefined,
                widget_orders: widgets.map((widget, index) => ({
                    widget_id: widget.id,
                    display_order: index + 1 // 1-based ordering
                }))
            };

            const response = await authFetch(`${this.baseUrl}/v1/widgets/order`, {
                method: 'PUT',
                body: JSON.stringify(orderData)
            });

            return response.ok;
        } catch (error) {
            console.error('Error saving widget order:', error);
            return false;
        }
    }

    /**
     * Fetch widgets with proper ordering
     * @param userId - User ID
     * @returns Promise<Widget[]> - Ordered widgets
     */
    static async fetchWidgetsOrdered(userId: string, collectionId?: string | null, reportTab?: string | null): Promise<Widget[]> {
        try {
            const connectionId = getConnectionId();

            let url = `${this.baseUrl}/v1/widgets/?user_id=${userId}&order_by=display_order`;

            // connection_id is omitted for live-module collections (they use connection_id=0)
            if (connectionId && !collectionId?.startsWith('live-module-')) {
                url += `&connection_id=${connectionId}`;
            }
            if (collectionId) {
                url += `&collection_id=${encodeURIComponent(collectionId)}`;
            }
            if (reportTab) {
                url += `&report_tab=${encodeURIComponent(reportTab)}`;
            }

            // authFetch transparently refreshes an expired access token and
            // retries once before giving up - a 401 here no longer means an
            // immediate hard redirect; the session survives silently in the
            // common case (see utils/api.ts).
            const response = await authFetch(url);

            if (!response.ok) {
                throw new Error('Failed to fetch widgets');
            }

            const widgets: Widget[] = await response.json();

            // Sort by display_order, fallback to created_at if no order is set
            // Handle null/undefined display_order by treating as high number for sorting
            return widgets.sort((a, b) => {
                const orderA = a.display_order ?? 999999;
                const orderB = b.display_order ?? 999999;

                if (orderA !== orderB) {
                    return orderA - orderB;
                }

                // If same display_order (or both null), sort by creation date
                return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
            });
        } catch (error) {
            console.error('Error fetching widgets:', error);
            throw error;
        }
    }

    /**
     * Debounced save function to avoid excessive API calls
     */
    private static saveOrderTimeout: NodeJS.Timeout | null = null;

    static saveWidgetOrderDebounced(userId: string, widgets: Widget[], collectionId?: string | null, reportTab?: string | null, delay: number = 1000): void {
        if (this.saveOrderTimeout) {
            clearTimeout(this.saveOrderTimeout);
        }

        this.saveOrderTimeout = setTimeout(async () => {
            const success = await this.saveWidgetOrder(userId, widgets, collectionId);
            if (!success) {
                console.warn('Failed to save widget order to backend');
                // You could show a toast notification here
            }
        }, delay);
    }
}
